import Rellax from 'rellax';
import classify from '../../helpers/classify';

export const MotionTypes = {
  None: 'none',
  FadeFromDark: 'fade-from-dark',
  FadeFromLight: 'fade-from-light',
  FadeFromBlur: 'fade-from-blur',
  KenBurnsEffect: 'ken-burns-effect',
  VerticalParallaxDrift: 'vertical-parallax-drift'
} as const;

export type MotionType = typeof MotionTypes[keyof typeof MotionTypes];
export interface MotionProps {
  motion: MotionType;
  parallaxScale: number;
}

type WithMotionOptions = {
  motion: MotionType;
  parallaxScale: number;
};

const PARALLAX_DEFAULT_SCALE = 1.5;
const MOTION = 'motion';
const ACTIVE = 'active';
const FADE_OVERLAY = 'fade-overlay';

const motion = {
  name: 'Motion Effect',
  defaultValue: MotionTypes.None,
  options: Object.values(MotionTypes),
  control: { type: 'radio' },
  table: { category: 'Motion' }
};

const parallaxScale = {
  name: 'Parallax Scale Factor',
  defaultValue: PARALLAX_DEFAULT_SCALE,
  control: { type: 'number' },
  table: { category: 'Motion' }
};

export const motionArgs = { motion, parallaxScale };

function createIntersectionObserver(): IntersectionObserver {
  const options = {
    root: null,
    rootMargin: '0% 0% -40% 0%',
    threshold: 0
  };

  const handleIntersect: IntersectionObserverCallback = (entries, observer): void => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(ACTIVE);
        observer.unobserve(entry.target);
      }
    });
  };

  return new IntersectionObserver(handleIntersect, options);
}

export const withMotion = (imageDiv: string, options: WithMotionOptions): string => {
  const motionClass = `${MOTION}--${options.motion}`;
  const datasetParallaxScale =
    options.motion === MotionTypes.VerticalParallaxDrift
      ? `data-parallax-scale="${options.parallaxScale || PARALLAX_DEFAULT_SCALE}"`
      : '';

  return `
  <div class="${MOTION} ${motionClass}" ${datasetParallaxScale}>
    ${imageDiv}
    <div class="${FADE_OVERLAY}"></div>
  </div>`;
};

function initMotion(el: HTMLElement, observer: IntersectionObserver): void {
  observer.observe(el);
  const parallaxClass = `${MOTION}--${MotionTypes.VerticalParallaxDrift}`;
  const isParallax = el.classList.contains(parallaxClass);
  if (isParallax) {
    const img = el.firstElementChild as HTMLElement;
    if (img) {
      img.style.setProperty('transform', `scale(${el.dataset.parallaxScale || PARALLAX_DEFAULT_SCALE})`);
      // eslint-disable-next-line no-new
      new Rellax(img, { center: true });
    }
  }
}

type Container = Document | HTMLElement;
export function initAllMotion(container: Container = document): void {
  const observer = createIntersectionObserver();
  const allCmsComponentsWithMotion = container.querySelectorAll<HTMLElement>(classify(MOTION));
  allCmsComponentsWithMotion.forEach((component) => initMotion(component, observer));
}
